import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot,
  Search,
  Sparkles,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Target,
  Lightbulb,
  AlertTriangle,
  Zap,
  Shield,
  RefreshCw,
  Newspaper,
  Users,
  Activity,
  Gauge,
  CheckCircle2,
  XCircle,
  DollarSign,
  Percent,
  LineChart,
  ExternalLink,
  Info,
  Layers,
} from 'lucide-react';
import { aiAPI } from '../../services/api';
import './HomeAIDiagnosisHero.css';

const HOT_SYMBOLS = ['AAPL', 'TSLA', 'NVDA', 'MSFT', 'GOOGL', 'AMZN', 'META'];

const LOADING_STEPS = [
  { key: 'quote', label: 'Real-time Price', icon: Zap, threshold: 10 },
  { key: 'technical', label: 'Technical Indicators', icon: Activity, threshold: 25 },
  { key: 'fundamentals', label: 'Fundamental Data', icon: BarChart3, threshold: 42 },
  { key: 'peers', label: 'Peer Valuation', icon: Users, threshold: 58 },
  { key: 'ratings', label: 'Analyst Ratings', icon: Newspaper, threshold: 72 },
  { key: 'ai', label: 'AI Thesis Generation', icon: Bot, threshold: 82 },
];

const fmt = (n, digits = 2) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toFixed(digits);
};
const fmtPct = (n, digits = 2) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toFixed(digits)}%`;
};
const fmtSigned = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}`;
};
const fmtMarketCap = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  // Finnhub returns market cap in millions of USD
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}T`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(2)}B`;
  if (v >= 1) return `$${v.toFixed(2)}M`;
  return `$${(v * 1000).toFixed(0)}K`;
};
const fmtRelDate = (iso) => {
  if (!iso) return '';
  const d = typeof iso === 'number' ? new Date(iso * 1000) : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const diffH = diffMs / 3_600_000;
  if (diffH < 1) return `${Math.max(1, Math.round(diffMs / 60_000))} min ago`;
  if (diffH < 24) return `${Math.round(diffH)} h ago`;
  const diffD = diffH / 24;
  if (diffD < 7) return `${Math.round(diffD)} d ago`;
  return d.toISOString().slice(0, 10);
};

// 6-dim scoring labels + colors
const SCORE_DIMS = [
  { key: 'value', label: 'Value' },
  { key: 'growth', label: 'Growth' },
  { key: 'quality', label: 'Quality' },
  { key: 'momentum', label: 'Momentum' },
  { key: 'technical', label: 'Technical' },
  { key: 'analyst', label: 'Analyst' },
];
const scoreClass = (v) => {
  if (v == null) return 'aidx-sc-na';
  if (v >= 70) return 'aidx-sc-high';
  if (v >= 40) return 'aidx-sc-mid';
  return 'aidx-sc-low';
};

const HomeAIDiagnosisHero = () => {
  const [symbol, setSymbol] = useState('');
  const [state, setState] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'
  const [report, setReport] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [progress, setProgress] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const progressTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, []);

  const startProgress = useCallback(() => {
    setProgress(0);
    setElapsedSec(0);
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    const startedAt = Date.now();
    progressTimerRef.current = setInterval(() => {
      const elapsedMs = Date.now() - startedAt;
      const seconds = elapsedMs / 1000;
      setElapsedSec(seconds);
      // Two-stage curve so users never see it "stuck":
      //   0-12s: exponential ramp to ~85%
      //   12s+: slow linear creep toward 97% (never reaches until API resolves)
      let p;
      if (elapsedMs < 12000) {
        p = 88 * (1 - Math.exp(-elapsedMs / 4500));
      } else {
        const extra = Math.min(9, (elapsedMs - 12000) / 1500); // +9% over ~13.5s
        p = 88 + extra;
      }
      setProgress(Math.min(97, p));
    }, 150);
  }, []);

  const stopProgress = useCallback((toValue = 100) => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    setProgress(toValue);
  }, []);

  const runDiagnosis = useCallback(async (sym) => {
    const upper = String(sym || '').trim().toUpperCase();
    if (!upper) {
      setErrorMsg('Please enter a stock symbol');
      return;
    }
    setErrorMsg('');
    setReport(null);
    setState('loading');
    startProgress();

    // Bump request id so any stale in-flight response is ignored
    const myReqId = ++requestIdRef.current;
    const isStale = () => !mountedRef.current || requestIdRef.current !== myReqId;

    try {
      const res = await aiAPI.diagnosisPro(upper);
      if (isStale()) return;
      if (res.data?.success && res.data.data) {
        stopProgress(100);
        setTimeout(() => {
          if (isStale()) return;
          setReport(res.data.data);
          setState('ready');
        }, 350);
      } else {
        stopProgress(0);
        setErrorMsg(res.data?.message || 'Failed to run diagnosis');
        setState('error');
      }
    } catch (err) {
      if (isStale()) return;
      stopProgress(0);
      const msg = err.response?.data?.message || err.message || 'AI diagnosis service unavailable';
      setErrorMsg(msg);
      setState('error');
    }
  }, [startProgress, stopProgress]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    runDiagnosis(symbol);
  };

  const handleReset = () => {
    setState('idle');
    setReport(null);
    setSymbol('');
    setErrorMsg('');
  };

  const changePercent = report?.quote?.changePercent != null
    ? parseFloat(report.quote.changePercent)
    : null;
  const priceUp = changePercent != null && changePercent >= 0;

  const overallScore = report?.scores?.overall ?? 0;
  const ratings = report?.ratings || null;
  const totalRatings = ratings?.total || 0;
  const bullishCount = ratings ? (ratings.strongBuy || 0) + (ratings.buy || 0) : 0;
  const bearishCount = ratings ? (ratings.sell || 0) + (ratings.strongSell || 0) : 0;

  const actionBadgeClass = (action) => {
    const a = String(action || '').toUpperCase();
    if (a === 'BUY') return 'aidx-badge aidx-badge--buy';
    if (a === 'SELL') return 'aidx-badge aidx-badge--sell';
    return 'aidx-badge aidx-badge--hold';
  };

  const verdictClass = (verdict, positive) => {
    const v = String(verdict || '').toLowerCase();
    if (positive.includes(v)) return 'aidx-verdict aidx-verdict--pos';
    if (['expensive', 'bearish'].includes(v)) return 'aidx-verdict aidx-verdict--neg';
    return 'aidx-verdict aidx-verdict--neutral';
  };

  return (
    <section className="aidx" aria-label="AI Stock Diagnosis">
      <div className="aidx__container">
        {/* Hero Header */}
        <div className="aidx__hero">
          <div className="aidx__hero-badge">
            <Bot size={14} />
            <span>Institutional-grade · Powered by AI</span>
          </div>
          <h2 className="aidx__hero-title">
            <span className="aidx__hero-title-line">AI Stock Diagnosis</span>
            <span className="aidx__hero-title-accent">Analyze any US stock in seconds</span>
          </h2>
          <p className="aidx__hero-desc">
            Real-time price · Technical indicators · Fundamentals · Peer valuation · Analyst ratings ·
            Multi-scenario targets — all in one AI-generated report.
          </p>
        </div>

        {/* State: IDLE — Search form */}
        <AnimatePresence mode="wait">
          {state === 'idle' && (
            <motion.div
              key="idle"
              className="aidx__idle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
            >
              <form className="aidx__search" onSubmit={handleSubmit}>
                <div className="aidx__search-icon">
                  <Search size={20} />
                </div>
                <input
                  type="text"
                  className="aidx__search-input"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase().replace(/[^A-Z0-9.-]/g, ''))}
                  placeholder="Enter a US ticker: AAPL, TSLA, NVDA…"
                  maxLength={10}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button type="submit" className="aidx__search-btn" disabled={!symbol.trim()}>
                  <Sparkles size={16} />
                  <span>Analyze</span>
                </button>
              </form>

              {errorMsg && (
                <div className="aidx__error">
                  <AlertTriangle size={14} />
                  <span>{errorMsg}</span>
                </div>
              )}

              <div className="aidx__hot">
                <span className="aidx__hot-label">Popular:</span>
                <div className="aidx__hot-pills">
                  {HOT_SYMBOLS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="aidx__pill"
                      onClick={() => {
                        setSymbol(s);
                        runDiagnosis(s);
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="aidx__features">
                <div className="aidx__feature">
                  <Gauge size={14} />
                  <span>6-dimension scoring</span>
                </div>
                <div className="aidx__feature">
                  <Target size={14} />
                  <span>3-scenario targets</span>
                </div>
                <div className="aidx__feature">
                  <Shield size={14} />
                  <span>Risk breakdown</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* State: LOADING — Progress + steps */}
          {state === 'loading' && (
            <motion.div
              key="loading"
              className="aidx__loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="aidx__loading-inner">
                <div className="aidx__loading-icon">
                  <Bot size={48} className="aidx__loading-bot" />
                  <div className="aidx__loading-ring" />
                  <div className="aidx__loading-ring aidx__loading-ring--2" />
                </div>
                <h3 className="aidx__loading-title">Analyzing {symbol}…</h3>
                <p className="aidx__loading-desc">
                  Fetching real-time price, computing technical indicators, aggregating fundamentals
                  and generating AI thesis.
                </p>

                <div className="aidx__loading-bar">
                  <div className="aidx__loading-bar-fill" style={{ width: `${progress}%` }} />
                </div>
                <div className="aidx__loading-percent">
                  <span>{Math.round(progress)}%</span>
                  <span className="aidx__loading-elapsed">· {elapsedSec.toFixed(1)}s elapsed</span>
                </div>

                <div className="aidx__steps">
                  {LOADING_STEPS.map((step, idx) => {
                    const Ico = step.icon;
                    const active = progress >= step.threshold;
                    const isCurrent =
                      active &&
                      (idx === LOADING_STEPS.length - 1 ||
                        progress < LOADING_STEPS[idx + 1].threshold);
                    return (
                      <div
                        key={step.key}
                        className={`aidx__step ${active ? 'aidx__step--active' : ''} ${isCurrent ? 'aidx__step--current' : ''}`}
                      >
                        <Ico size={16} />
                        <span>{step.label}</span>
                        {isCurrent && <span className="aidx__step-dot" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* State: ERROR */}
          {state === 'error' && (
            <motion.div
              key="error"
              className="aidx__error-block"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <AlertTriangle size={24} />
              <div>
                <h4>Diagnosis Failed</h4>
                <p>{errorMsg || 'Something went wrong. Please try again.'}</p>
              </div>
              <button className="aidx__retry" onClick={handleReset}>
                <RefreshCw size={14} />
                <span>Try Again</span>
              </button>
            </motion.div>
          )}

          {/* State: READY — Compact report */}
          {state === 'ready' && report && (
            <motion.div
              key="ready"
              className="aidx__report"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Top bar: symbol + price + score */}
              <div className="aidx__report-top">
                <div className="aidx__report-symbol">
                  <div className="aidx__report-symbol-main">
                    <span className="aidx__report-ticker">{report.symbol}</span>
                    <span className="aidx__report-name">{report.profile?.name || ''}</span>
                  </div>
                  <div className="aidx__report-industry">
                    {report.profile?.industry || 'US Equity'} · {report.profile?.exchange || 'US'}
                  </div>
                </div>

                <div className="aidx__report-price">
                  <div className="aidx__report-price-main">${fmt(report.quote?.price)}</div>
                  {changePercent != null && (
                    <div className={`aidx__report-change ${priceUp ? 'aidx__report-change--up' : 'aidx__report-change--down'}`}>
                      {priceUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                      <span>{fmtSigned(report.quote?.change)}</span>
                      <span className="aidx__report-change-pct">({fmtPct(changePercent)})</span>
                    </div>
                  )}
                </div>

                <div className="aidx__score">
                  <div className="aidx__score-ring" data-score={overallScore >= 70 ? 'high' : overallScore >= 40 ? 'mid' : 'low'}>
                    <span className="aidx__score-value">{overallScore}</span>
                    <span className="aidx__score-max">/100</span>
                  </div>
                  <span className="aidx__score-label">AI Score</span>
                </div>
              </div>

              {/* Verdicts row */}
              <div className="aidx__verdicts">
                {report.valuationVerdict && (
                  <div className={verdictClass(report.valuationVerdict, ['cheap', 'fair'])}>
                    <span className="aidx__verdict-label">Valuation</span>
                    <span className="aidx__verdict-value">{report.valuationVerdict}</span>
                  </div>
                )}
                {report.technicalVerdict && (
                  <div className={verdictClass(report.technicalVerdict, ['bullish', 'neutral'])}>
                    <span className="aidx__verdict-label">Technical</span>
                    <span className="aidx__verdict-value">{report.technicalVerdict}</span>
                  </div>
                )}
                {report.actionPlan?.action && (
                  <div className={actionBadgeClass(report.actionPlan.action)}>
                    <span className="aidx__verdict-label">Action</span>
                    <span className="aidx__verdict-value">{report.actionPlan.action}</span>
                  </div>
                )}
              </div>

              {/* 6-Dimension Score Bars */}
              {report.scores && (
                <div className="aidx__scoreboard">
                  <div className="aidx__section-head">
                    <Gauge size={14} />
                    <span>6-Dimension Scoring</span>
                    <span className="aidx__section-hint">Higher is better · 0-100</span>
                  </div>
                  <div className="aidx__score-bars">
                    {SCORE_DIMS.map((dim) => {
                      const v = report.scores[dim.key];
                      return (
                        <div key={dim.key} className={`aidx__score-row ${scoreClass(v)}`}>
                          <span className="aidx__score-row-label">{dim.label}</span>
                          <div className="aidx__score-row-track">
                            <div
                              className="aidx__score-row-fill"
                              style={{ width: v != null ? `${v}%` : '0%' }}
                            />
                          </div>
                          <span className="aidx__score-row-val">{v != null ? v : 'N/A'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 3-card metric grid */}
              <div className="aidx__cards">
                {/* Valuation card */}
                <div className="aidx__card">
                  <div className="aidx__card-head">
                    <BarChart3 size={14} />
                    <span>Valuation</span>
                  </div>
                  <div className="aidx__card-metrics">
                    <div>
                      <div className="aidx__metric-label">P/E TTM</div>
                      <div className="aidx__metric-value">{fmt(report.metrics?.peTTM)}</div>
                    </div>
                    <div>
                      <div className="aidx__metric-label">Peer Percentile</div>
                      <div className="aidx__metric-value">
                        {report.peerValuation?.peRank != null
                          ? `${Math.round(report.peerValuation.peRank * 100)}%`
                          : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="aidx__metric-label">ROE TTM</div>
                      <div className="aidx__metric-value">{fmtPct(report.metrics?.roeTTM)}</div>
                    </div>
                  </div>
                </div>

                {/* Technical card */}
                <div className="aidx__card">
                  <div className="aidx__card-head">
                    <Activity size={14} />
                    <span>Technical</span>
                  </div>
                  <div className="aidx__card-metrics">
                    <div>
                      <div className="aidx__metric-label">RSI(14)</div>
                      <div className="aidx__metric-value">{fmt(report.technical?.rsi14, 1)}</div>
                    </div>
                    <div>
                      <div className="aidx__metric-label">MA20</div>
                      <div className="aidx__metric-value">${fmt(report.technical?.ma?.ma20)}</div>
                    </div>
                    <div>
                      <div className="aidx__metric-label">MACD</div>
                      <div className="aidx__metric-value aidx__metric-value--small">
                        {report.technical?.macd?.cross === 'golden' ? '↗ Golden' :
                         report.technical?.macd?.cross === 'death' ? '↘ Death' :
                         report.technical?.macd?.histogram > 0 ? '↑ Positive' :
                         report.technical?.macd?.histogram < 0 ? '↓ Negative' : '—'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Analyst ratings card */}
                <div className="aidx__card">
                  <div className="aidx__card-head">
                    <Users size={14} />
                    <span>Analyst Consensus</span>
                  </div>
                  {ratings && totalRatings > 0 ? (
                    <>
                      <div className="aidx__rating-bar">
                        <div
                          className="aidx__rating-bar-buy"
                          style={{ width: `${(bullishCount / totalRatings) * 100}%` }}
                        />
                        <div
                          className="aidx__rating-bar-hold"
                          style={{ width: `${((ratings.hold || 0) / totalRatings) * 100}%` }}
                        />
                        <div
                          className="aidx__rating-bar-sell"
                          style={{ width: `${(bearishCount / totalRatings) * 100}%` }}
                        />
                      </div>
                      <div className="aidx__rating-legend">
                        <span className="aidx__rating-item aidx__rating-item--buy">Buy {bullishCount}</span>
                        <span className="aidx__rating-item aidx__rating-item--hold">Hold {ratings.hold || 0}</span>
                        <span className="aidx__rating-item aidx__rating-item--sell">Sell {bearishCount}</span>
                      </div>
                    </>
                  ) : (
                    <div className="aidx__no-data">No analyst data</div>
                  )}
                </div>
              </div>

              {/* AI Thesis */}
              {report.aiThesis && (
                <div className="aidx__thesis">
                  <div className="aidx__thesis-head">
                    <Lightbulb size={14} />
                    <span>AI Investment Thesis</span>
                  </div>
                  <p className="aidx__thesis-body">{report.aiThesis}</p>
                </div>
              )}

              {/* Strengths & Risks */}
              {((Array.isArray(report.keyStrengths) && report.keyStrengths.length > 0) ||
                (report.keyRisks && (report.keyRisks.shortTerm || report.keyRisks.longTerm || report.keyRisks.specific))) && (
                <div className="aidx__sr-grid">
                  {Array.isArray(report.keyStrengths) && report.keyStrengths.length > 0 && (
                    <div className="aidx__sr aidx__sr--strengths">
                      <div className="aidx__section-head aidx__section-head--tight">
                        <CheckCircle2 size={14} />
                        <span>Key Strengths</span>
                      </div>
                      <ul className="aidx__sr-list">
                        {report.keyStrengths.slice(0, 5).map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {report.keyRisks && (report.keyRisks.shortTerm || report.keyRisks.longTerm || report.keyRisks.specific) && (
                    <div className="aidx__sr aidx__sr--risks">
                      <div className="aidx__section-head aidx__section-head--tight">
                        <XCircle size={14} />
                        <span>Key Risks</span>
                      </div>
                      <ul className="aidx__sr-list">
                        {report.keyRisks.shortTerm && (
                          <li><b>Short-term:</b> {report.keyRisks.shortTerm}</li>
                        )}
                        {report.keyRisks.longTerm && (
                          <li><b>Long-term:</b> {report.keyRisks.longTerm}</li>
                        )}
                        {report.keyRisks.specific && (
                          <li><b>Specific:</b> {report.keyRisks.specific}</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Action Plan + Scenarios */}
              {report.actionPlan && (
                <div className="aidx__action-plan">
                  <div className="aidx__action-plan-grid">
                    <div>
                      <div className="aidx__ap-label">Target Price</div>
                      <div className="aidx__ap-value">${fmt(report.actionPlan.targetPrice)}</div>
                    </div>
                    <div>
                      <div className="aidx__ap-label">Stop Loss</div>
                      <div className="aidx__ap-value">${fmt(report.actionPlan.stopLoss)}</div>
                    </div>
                    <div>
                      <div className="aidx__ap-label">Position Size</div>
                      <div className="aidx__ap-value aidx__ap-value--small">{report.actionPlan.positionSize || '—'}</div>
                    </div>
                    <div>
                      <div className="aidx__ap-label">Holding Period</div>
                      <div className="aidx__ap-value aidx__ap-value--small">{report.actionPlan.holdingPeriod || '—'}</div>
                    </div>
                  </div>
                  {report.scenarios && (
                    <div className="aidx__scenarios">
                      <div className="aidx__scenario aidx__scenario--bull">
                        <span className="aidx__scenario-label">Bull ({Math.round((report.scenarios.bull?.probability || 0) * 100)}%)</span>
                        <span className="aidx__scenario-price">${fmt(report.scenarios.bull?.targetPrice)}</span>
                      </div>
                      <div className="aidx__scenario aidx__scenario--base">
                        <span className="aidx__scenario-label">Base ({Math.round((report.scenarios.base?.probability || 0) * 100)}%)</span>
                        <span className="aidx__scenario-price">${fmt(report.scenarios.base?.targetPrice)}</span>
                      </div>
                      <div className="aidx__scenario aidx__scenario--bear">
                        <span className="aidx__scenario-label">Bear ({Math.round((report.scenarios.bear?.probability || 0) * 100)}%)</span>
                        <span className="aidx__scenario-price">${fmt(report.scenarios.bear?.targetPrice)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ==================== FINANCIAL SNAPSHOT ==================== */}
              {report.metrics && (
                <div className="aidx__panel">
                  <div className="aidx__section-head">
                    <DollarSign size={14} />
                    <span>Financial Snapshot</span>
                    {report.profile?.marketCapitalization && (
                      <span className="aidx__section-hint">Market Cap {fmtMarketCap(report.profile.marketCapitalization)}</span>
                    )}
                  </div>
                  <div className="aidx__fin-grid">
                    <div className="aidx__fin-item">
                      <div className="aidx__fin-label">52W Range</div>
                      <div className="aidx__fin-value">
                        ${fmt(report.metrics.week52Low)} — ${fmt(report.metrics.week52High)}
                      </div>
                      {report.quote?.price != null && report.metrics.week52Low != null && report.metrics.week52High != null && (
                        <div className="aidx__fin-track">
                          <div
                            className="aidx__fin-track-dot"
                            style={{
                              left: `${Math.max(0, Math.min(100,
                                ((report.quote.price - report.metrics.week52Low) /
                                  Math.max(0.01, (report.metrics.week52High - report.metrics.week52Low))) * 100
                              ))}%`,
                            }}
                          />
                        </div>
                      )}
                    </div>
                    {report.metrics.revenueGrowthTTMYoy != null && (
                      <div className="aidx__fin-item">
                        <div className="aidx__fin-label">Revenue Growth (TTM)</div>
                        <div className={`aidx__fin-value ${report.metrics.revenueGrowthTTMYoy > 0 ? 'aidx__fin-value--pos' : report.metrics.revenueGrowthTTMYoy < 0 ? 'aidx__fin-value--neg' : ''}`}>
                          {fmtPct(report.metrics.revenueGrowthTTMYoy)}
                        </div>
                      </div>
                    )}
                    {report.metrics.epsGrowthTTMYoy != null && (
                      <div className="aidx__fin-item">
                        <div className="aidx__fin-label">EPS Growth (TTM)</div>
                        <div className={`aidx__fin-value ${report.metrics.epsGrowthTTMYoy > 0 ? 'aidx__fin-value--pos' : report.metrics.epsGrowthTTMYoy < 0 ? 'aidx__fin-value--neg' : ''}`}>
                          {fmtPct(report.metrics.epsGrowthTTMYoy)}
                        </div>
                      </div>
                    )}
                    {report.metrics.grossMarginTTM != null && (
                      <div className="aidx__fin-item">
                        <div className="aidx__fin-label">Gross Margin</div>
                        <div className="aidx__fin-value">{fmtPct(report.metrics.grossMarginTTM)}</div>
                      </div>
                    )}
                    {report.metrics.netProfitMarginTTM != null && (
                      <div className="aidx__fin-item">
                        <div className="aidx__fin-label">Net Margin</div>
                        <div className="aidx__fin-value">{fmtPct(report.metrics.netProfitMarginTTM)}</div>
                      </div>
                    )}
                    {report.metrics.pbAnnual != null && (
                      <div className="aidx__fin-item">
                        <div className="aidx__fin-label">P/B</div>
                        <div className="aidx__fin-value">{fmt(report.metrics.pbAnnual)}</div>
                      </div>
                    )}
                    {report.metrics.psTTM != null && (
                      <div className="aidx__fin-item">
                        <div className="aidx__fin-label">P/S (TTM)</div>
                        <div className="aidx__fin-value">{fmt(report.metrics.psTTM)}</div>
                      </div>
                    )}
                    {report.metrics.pegRatio != null && (
                      <div className="aidx__fin-item">
                        <div className="aidx__fin-label">PEG</div>
                        <div className="aidx__fin-value">{fmt(report.metrics.pegRatio)}</div>
                      </div>
                    )}
                    {report.metrics.dividendYieldIndicatedAnnual != null && (
                      <div className="aidx__fin-item">
                        <div className="aidx__fin-label">Dividend Yield</div>
                        <div className="aidx__fin-value">{fmtPct(report.metrics.dividendYieldIndicatedAnnual)}</div>
                      </div>
                    )}
                    {report.metrics.beta != null && (
                      <div className="aidx__fin-item">
                        <div className="aidx__fin-label">Beta</div>
                        <div className="aidx__fin-value">{fmt(report.metrics.beta)}</div>
                      </div>
                    )}
                    {report.metrics.totalDebt2EquityAnnual != null && (
                      <div className="aidx__fin-item">
                        <div className="aidx__fin-label">Debt / Equity</div>
                        <div className="aidx__fin-value">{fmt(report.metrics.totalDebt2EquityAnnual)}</div>
                      </div>
                    )}
                    {report.metrics.week52PriceReturnDaily != null && (
                      <div className="aidx__fin-item">
                        <div className="aidx__fin-label">1Y Return</div>
                        <div className={`aidx__fin-value ${report.metrics.week52PriceReturnDaily > 0 ? 'aidx__fin-value--pos' : report.metrics.week52PriceReturnDaily < 0 ? 'aidx__fin-value--neg' : ''}`}>
                          {fmtPct(report.metrics.week52PriceReturnDaily)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ==================== PEER VALUATION ==================== */}
              {report.peerValuation && Array.isArray(report.peerValuation.peers) && report.peerValuation.peers.length > 0 && (
                <div className="aidx__panel">
                  <div className="aidx__section-head">
                    <Layers size={14} />
                    <span>Peer Valuation Comparison</span>
                    {report.peerValuation.peRank != null && (
                      <span className="aidx__section-hint">
                        {report.symbol} P/E is at {Math.round(report.peerValuation.peRank * 100)}th percentile
                      </span>
                    )}
                  </div>
                  <div className="aidx__peers-table">
                    <div className="aidx__peers-head">
                      <span>Ticker</span>
                      <span>P/E</span>
                      <span>P/B</span>
                      <span>Market Cap</span>
                    </div>
                    {report.peerValuation.peers.slice(0, 6).map((p) => (
                      <div
                        key={p.symbol}
                        className={`aidx__peers-row ${p.symbol === report.symbol ? 'aidx__peers-row--self' : ''}`}
                      >
                        <span className="aidx__peers-tick">
                          {p.symbol}{p.symbol === report.symbol && <span className="aidx__peers-you">YOU</span>}
                        </span>
                        <span>{fmt(p.pe)}</span>
                        <span>{fmt(p.pb)}</span>
                        <span>{fmtMarketCap(p.marketCap)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ==================== TECHNICAL DEEP ==================== */}
              {report.technical && (
                <div className="aidx__panel">
                  <div className="aidx__section-head">
                    <LineChart size={14} />
                    <span>Technical Deep-Dive</span>
                    <span className="aidx__section-hint">{report.technical.candlesCount || 0} daily candles analyzed</span>
                  </div>
                  <div className="aidx__tech-grid">
                    <div className="aidx__tech-block">
                      <div className="aidx__tech-title">Moving Averages</div>
                      <div className="aidx__tech-kv">
                        {report.technical.ma?.ma5 != null && (<><span>MA5</span><b>${fmt(report.technical.ma.ma5)}</b></>)}
                        {report.technical.ma?.ma10 != null && (<><span>MA10</span><b>${fmt(report.technical.ma.ma10)}</b></>)}
                        {report.technical.ma?.ma20 != null && (<><span>MA20</span><b>${fmt(report.technical.ma.ma20)}</b></>)}
                        {report.technical.ma?.ma60 != null && (<><span>MA60</span><b>${fmt(report.technical.ma.ma60)}</b></>)}
                      </div>
                    </div>
                    {report.technical.boll && (
                      <div className="aidx__tech-block">
                        <div className="aidx__tech-title">Bollinger Bands (20)</div>
                        <div className="aidx__tech-kv">
                          <span>Upper</span><b>${fmt(report.technical.boll.upper)}</b>
                          <span>Middle</span><b>${fmt(report.technical.boll.middle)}</b>
                          <span>Lower</span><b>${fmt(report.technical.boll.lower)}</b>
                        </div>
                      </div>
                    )}
                    <div className="aidx__tech-block">
                      <div className="aidx__tech-title">Support / Resistance</div>
                      <div className="aidx__tech-kv">
                        {report.technical.support != null && (<><span>Support</span><b>${fmt(report.technical.support)}</b></>)}
                        {report.technical.resistance != null && (<><span>Resistance</span><b>${fmt(report.technical.resistance)}</b></>)}
                        {report.technical.rsi14 != null && (<><span>RSI(14)</span><b>{fmt(report.technical.rsi14, 1)}</b></>)}
                      </div>
                    </div>
                    {report.technical.volume && (
                      <div className="aidx__tech-block">
                        <div className="aidx__tech-title">Volume Analysis</div>
                        <div className="aidx__tech-kv">
                          <span>Avg (20d)</span><b>{(report.technical.volume.avgVolume / 1_000_000).toFixed(1)}M</b>
                          <span>Latest</span><b>{(report.technical.volume.latestVolume / 1_000_000).toFixed(1)}M</b>
                          {report.technical.volume.ratio != null && (
                            <>
                              <span>Vs. Avg</span>
                              <b className={report.technical.volume.ratio > 1.5 ? 'aidx__fin-value--pos' : report.technical.volume.ratio < 0.7 ? 'aidx__fin-value--neg' : ''}>
                                {report.technical.volume.ratio > 1.5 ? `↑ ${report.technical.volume.ratio.toFixed(2)}× Spike` :
                                 report.technical.volume.ratio < 0.7 ? `↓ ${report.technical.volume.ratio.toFixed(2)}× Shrink` :
                                 `${report.technical.volume.ratio.toFixed(2)}× Normal`}
                              </b>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {Array.isArray(report.technical.signals) && report.technical.signals.length > 0 && (
                    <div className="aidx__signals">
                      {report.technical.signals.map((s, i) => (
                        <span key={i} className={`aidx__signal aidx__signal--${s.bias || 'neutral'}`}>
                          {s.bias === 'bullish' && '↗ '}
                          {s.bias === 'bearish' && '↘ '}
                          {s.bias === 'neutral' && '→ '}
                          {s.text || s.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ==================== RECENT NEWS ==================== */}
              {Array.isArray(report.news) && report.news.length > 0 && (
                <div className="aidx__panel">
                  <div className="aidx__section-head">
                    <Newspaper size={14} />
                    <span>Recent News</span>
                    <span className="aidx__section-hint">{report.news.length} articles · past 7 days</span>
                  </div>
                  <div className="aidx__news-list">
                    {report.news.slice(0, 5).map((n, i) => (
                      <a
                        key={i}
                        href={n.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aidx__news-item"
                      >
                        <div className="aidx__news-body">
                          <div className="aidx__news-headline">{n.headline}</div>
                          <div className="aidx__news-meta">
                            <span>{n.source || 'News'}</span>
                            <span>·</span>
                            <span>{fmtRelDate(n.datetime)}</span>
                          </div>
                        </div>
                        <ExternalLink size={13} className="aidx__news-arrow" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* ==================== META FOOTER ==================== */}
              <div className="aidx__meta">
                <span><Info size={11} /> Source: {report.sources?.quote || 'itick'} quote · {report.sources?.technical || 'itick'} candles · {report.sources?.fundamentals || 'finnhub'} fundamentals</span>
                {report._totalMs != null && <span>· {(report._totalMs / 1000).toFixed(1)}s to build</span>}
                {report._servedFromCache && <span>· ⚡ served from cache</span>}
              </div>

              {/* Footer actions */}
              <div className="aidx__actions">
                <button className="aidx__cta aidx__cta--primary" onClick={handleReset}>
                  <RefreshCw size={16} />
                  <span>Analyze Another Stock</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default HomeAIDiagnosisHero;
